import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import { AdminPickCell, AdminPlainBar, AdminRowDelete, useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import './CustomerMaster.scss';

const EMPTY = { name: '', description: '', workType: 'tender', isActive: true };

const TimesheetActivitiesPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  useDocumentTitle('Timesheet activities');

  const load = () => platformAPI.listTimesheetActivities()
    .then((response) => setRows(response.data?.data?.activities || []))
    .catch((error) => setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to load activities' }))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const admin = useAdminRecordDelete({
    entity: 'activity',
    noun: 'activity',
    plural: 'activities',
    reload: load,
    label: (row) => row.name
  });
  const revision = useMasterRevision({ entity: 'activity' });

  const save = async (event) => {
    event.preventDefault();
    try {
      const body = form.id ? { ...form } : { ...form, id: undefined };
      if (form.id) {
        const changeReason = await revision.askReason(form.revision);
        if (!changeReason) return;
        body.changeReason = changeReason;
      }
      await platformAPI.saveTimesheetActivity(body);
      setForm(EMPTY);
      setMessage({ variant: 'success', text: 'Activity saved. Inactive activities stay on old entries and drop out of new ones.' });
      await load();
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to save the activity' });
    }
  };

  if (!canManageMasters(user?.permissions, user?.roles)) return <Navigate to="/apps/tender" replace />;
  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}>
        <BiArrowBack />
        Masters
      </button>
      <h1 className="h4">Timesheet activities</h1>
      <p className="text-muted">Reusable work types for tender time, assignment time, or both.</p>
      {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
      {admin.notice}
      <form className="cust-fields mb-3" onSubmit={save}>
        <Form.Group className="cust-field cust-field--4">
          <Form.Label>Name</Form.Label>
          <Form.Control value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
        </Form.Group>
        <Form.Group className="cust-field cust-field--4">
          <Form.Label>Applies to</Form.Label>
          <Form.Select value={form.workType} onChange={(event) => setForm({ ...form, workType: event.target.value })}>
            <option value="tender">Tender</option>
            <option value="assignment">Assignment</option>
            <option value="both">Both</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="cust-field cust-field--4">
          <Form.Label>Status</Form.Label>
          <Form.Select value={form.isActive ? 'active' : 'inactive'} onChange={(event) => setForm({ ...form, isActive: event.target.value === 'active' })}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="cust-field cust-field--8">
          <Form.Label>Description</Form.Label>
          <Form.Control value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Form.Group>
        <div className="cust-field cust-field--4 d-flex align-items-end">
          <Button type="submit" size="sm">{form.id ? 'Update' : 'Add activity'}</Button>
        </div>
      </form>
      <AdminPlainBar admin={admin} rows={rows} />
      <Table responsive size="sm">
        <thead><tr>{admin.enabled ? <th /> : null}<th>Name</th><th>Applies to</th><th>Rev.</th><th>Status</th><th /></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id}>
              <AdminPickCell admin={admin} id={row._id} />
              <td>{row.name}<div className="text-muted small">{row.description}</div></td>
              <td>{row.workType}</td>
              <td>{row.revision || 1}</td>
              <td>
                <span className={`cust-status cust-status--${row.isActive ? 'active' : 'inactive'}`}>
                  {row.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <Button size="sm" variant="outline-primary" onClick={() => setForm({ id: row._id, name: row.name, description: row.description || '', workType: row.workType, isActive: row.isActive !== false, code: row.code, revision: row.revision || 1 })}>Edit</Button>
                <Button size="sm" variant="link" onClick={() => revision.open(row)}>Revision info</Button>
                <AdminRowDelete admin={admin} id={row._id} label={row.name} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default TimesheetActivitiesPage;
